/**
 * Utilities for handling obfuscated JavaScript errors
 * Prevents ReferenceError from undefined obfuscated variables
 */

/**
 * Safely checks if a variable exists in the global scope
 * @param {string} variableName - The variable name to check
 * @param {any} fallback - Fallback value if variable doesn't exist
 * @returns {any} - Variable value or fallback
 */
export const safeGetGlobalVariable = (variableName, fallback = null) => {
    try {
        if (typeof window !== 'undefined' && window[variableName] !== undefined) {
            return window[variableName];
        }
        return fallback;
    } catch (error) {
        console.warn(`Variable ${variableName} is not accessible:`, error);
        return fallback;
    }
};

/**
 * Safely executes a function with obfuscated variable names
 * @param {Function} func - The function to execute
 * @param {Array} args - Arguments to pass to the function
 * @param {any} fallback - Fallback value if execution fails
 * @returns {any} - Function result or fallback
 */
export const safeExecuteObfuscatedFunction = (func, args = [], fallback = null) => {
    try {
        if (typeof func !== 'function') {
            console.warn('Attempted to execute non-function:', typeof func);
            return fallback;
        }
        
        return func(...args);
    } catch (error) {
        if (error.name === 'ReferenceError' && error.message.includes('is not defined')) {
            console.warn('Obfuscated variable reference error:', error.message);
        } else {
            console.error('Error executing obfuscated function:', error);
        }
        return fallback;
    }
};

/**
 * Creates a safe wrapper for obfuscated code execution
 * @param {Function} codeBlock - The code block to wrap
 * @param {any} fallback - Fallback value if execution fails
 * @returns {Function} - Wrapped function
 */
export const wrapObfuscatedCode = (codeBlock, fallback = null) => {
    return (...args) => {
        try {
            return codeBlock(...args);
        } catch (error) {
            if (error.name === 'ReferenceError') {
                console.warn('Obfuscated code reference error:', error.message);
                
                // Log to Sentry if available
                if (window.Sentry) {
                    window.Sentry.captureException(error, {
                        tags: {
                            errorType: 'OBFUSCATED_REFERENCE_ERROR',
                            context: 'OBFUSCATED_CODE_WRAPPER',
                            variableName: error.message.match(/(\w+) is not defined/)?.[1] || 'unknown'
                        }
                    });
                }
            } else {
                console.error('Error in obfuscated code:', error);
            }
            
            return fallback;
        }
    };
};

/**
 * Sets up global error handling for obfuscated JavaScript errors
 */
export const setupObfuscationErrorHandler = () => {
    // Monitor for ReferenceError with obfuscated variable names
    window.addEventListener('error', (event) => {
        if (event.error && 
            event.error.name === 'ReferenceError' && 
            event.error.message && 
            (/_0x[a-f0-9]+ is not defined/.test(event.error.message) ||
             /translate_http/.test(event.error.message) ||
             /sw\/<\(/.test(event.error.message))) {
            
            console.warn('Obfuscated variable reference error detected:', event.error.message);
            
            // Log to Sentry if available
            if (window.Sentry) {
                window.Sentry.captureException(event.error, {
                    tags: {
                        errorType: 'OBFUSCATED_REFERENCE_ERROR',
                        context: 'GLOBAL_ERROR_HANDLER',
                        variableName: event.error.message.match(/(\w+) is not defined/)?.[1] || 'unknown',
                        isTranslationError: /translate_http/.test(event.error.message)
                    }
                });
            }
            
            // Prevent the error from crashing the app
            event.preventDefault();
        }
    });
};

/**
 * Safely handles third-party script loading
 * @param {string} scriptUrl - The script URL to load
 * @param {Function} onLoad - Callback when script loads
 * @param {Function} onError - Callback when script fails to load
 * @returns {Promise} - Promise that resolves when script loads
 */
export const safeLoadScript = (scriptUrl, onLoad = null, onError = null) => {
    return new Promise((resolve, reject) => {
        try {
            const script = document.createElement('script');
            script.src = scriptUrl;
            script.async = true;
            
            script.onload = () => {
                console.log(`Script loaded successfully: ${scriptUrl}`);
                if (onLoad) {
                    try {
                        onLoad();
                    } catch (error) {
                        console.warn('Error in script onLoad callback:', error);
                    }
                }
                resolve();
            };
            
            script.onerror = (error) => {
                console.warn(`Script failed to load: ${scriptUrl}`, error);
                if (onError) {
                    try {
                        onError(error);
                    } catch (callbackError) {
                        console.warn('Error in script onError callback:', callbackError);
                    }
                }
                reject(error);
            };
            
            document.head.appendChild(script);
        } catch (error) {
            console.error('Error creating script element:', error);
            reject(error);
        }
    });
};

/**
 * Safely handles Google Tag Manager initialization
 * @param {string} gtmId - The GTM ID
 * @returns {Promise} - Promise that resolves when GTM is initialized
 */
export const safeInitializeGTM = (gtmId) => {
    return new Promise((resolve) => {
        try {
            // Check if GTM is already loaded
            if (window.gtag) {
                console.log('GTM already initialized');
                resolve();
                return;
            }
            
            // Load GTM script safely
            safeLoadScript(`https://www.googletagmanager.com/gtag/js?id=${gtmId}`)
                .then(() => {
                    // Initialize GTM
                    window.dataLayer = window.dataLayer || [];
                    window.gtag = function() {
                        window.dataLayer.push(arguments);
                    };
                    window.gtag('js', new Date());
                    window.gtag('config', gtmId);
                    
                    console.log('GTM initialized successfully');
                    resolve();
                })
                .catch((error) => {
                    console.warn('GTM initialization failed:', error);
                    resolve(); // Resolve anyway to prevent blocking
                });
        } catch (error) {
            console.error('Error initializing GTM:', error);
            resolve(); // Resolve anyway to prevent blocking
        }
    });
};

/**
 * Safely handles Trustpilot widget initialization
 * @returns {Promise} - Promise that resolves when Trustpilot is initialized
 */
export const safeInitializeTrustpilot = () => {
    return new Promise((resolve) => {
        try {
            // Check if Trustpilot is already loaded
            if (window.Trustpilot) {
                console.log('Trustpilot already initialized');
                resolve();
                return;
            }
            
            // Load Trustpilot script safely
            safeLoadScript('https://widget.trustpilot.com/bootstrap/v5/tp.widget.bundle.min.js')
                .then(() => {
                    console.log('Trustpilot initialized successfully');
                    resolve();
                })
                .catch((error) => {
                    console.warn('Trustpilot initialization failed:', error);
                    resolve(); // Resolve anyway to prevent blocking
                });
        } catch (error) {
            console.error('Error initializing Trustpilot:', error);
            resolve(); // Resolve anyway to prevent blocking
        }
    });
};

/**
 * Sets up safe initialization for all third-party scripts
 */
export const setupSafeThirdPartyScripts = () => {
    // Initialize GTM if GTM_ID is available
    if (window.GTM_ID) {
        safeInitializeGTM(window.GTM_ID);
    }
    
    // Initialize Trustpilot
    safeInitializeTrustpilot();
    
    // Set up obfuscation error handling
    setupObfuscationErrorHandler();
};

export default {
    safeGetGlobalVariable,
    safeExecuteObfuscatedFunction,
    wrapObfuscatedCode,
    setupObfuscationErrorHandler,
    safeLoadScript,
    safeInitializeGTM,
    safeInitializeTrustpilot,
    setupSafeThirdPartyScripts
};
